import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { crmCampaignsAPI } from '../../services/api';

// `withTickets`: add/remove carry a positive ticket count, exclude carries none — the backend
// enforces the same rule.
const EXCEPTION_TYPES = [
  { value: 'add_tickets',    label: 'Add tickets',    badge: 'success',     withTickets: true },
  { value: 'remove_tickets', label: 'Remove tickets', badge: 'warning',     withTickets: true },
  { value: 'exclude',        label: 'Exclude',        badge: 'destructive', withTickets: false },
];
const typeInfo = (value) => EXCEPTION_TYPES.find((t) => t.value === value);

const PAGE_SIZE = 25;
// reason is varchar(500) in Redshift, which counts bytes — so measure UTF-8 bytes, not characters.
const MAX_REASON_BYTES = 500;
const byteLength = (s) => new TextEncoder().encode(s).length;

const isPositiveInt = (s) => /^\d+$/.test(String(s).trim()) && Number(s) > 0;

// The backend refuses more than MAX_TICKETS; above CONFIRM_TICKETS the number is typed twice, so
// a slipped digit is caught before it is saved.
const MAX_TICKETS = 50000;
const CONFIRM_TICKETS = 10000;
const apiError = (err, fallback) => err?.response?.data?.error || fallback;

const FieldLabel = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">{children}</span>
);

// Add and edit share one form. When editing, player and tournament are the row's identity and
// stay read-only — moving an exception means deleting it and adding a new one.
function ExceptionFormDialog({ open, initial, onClose, onSaved }) {
  const editing = Boolean(initial);
  const [userId, setUserId] = useState('');
  const [tournamentId, setTournamentId] = useState('');
  const [type, setType] = useState('add_tickets');
  const [tickets, setTickets] = useState('');
  const [ticketsAgain, setTicketsAgain] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setUserId(initial ? String(initial.user_id) : '');
    setTournamentId(initial ? String(initial.tournament_id) : '');
    setType(initial?.exception_type || 'add_tickets');
    setTickets(initial?.tickets_number_change != null ? String(initial.tickets_number_change) : '');
    setTicketsAgain('');
    setReason(initial?.reason || '');
    setError('');
  }, [open, initial]);

  const needsTickets = typeInfo(type)?.withTickets;
  const reasonBytes = byteLength(reason.trim());
  const reasonTooLong = reasonBytes > MAX_REASON_BYTES;
  const ticketsValid = isPositiveInt(tickets) && Number(tickets) <= MAX_TICKETS;
  const ticketsTooMany = isPositiveInt(tickets) && Number(tickets) > MAX_TICKETS;
  // Asked again only for a new number: an unchanged large value was confirmed when it was saved.
  const needsConfirm = needsTickets && ticketsValid && Number(tickets) > CONFIRM_TICKETS
    && Number(tickets) !== initial?.tickets_number_change;
  const confirmed = !needsConfirm || ticketsAgain.trim() === tickets.trim();
  const valid = isPositiveInt(userId) && isPositiveInt(tournamentId)
    && (!needsTickets || ticketsValid) && confirmed && !reasonTooLong;

  const submit = async () => {
    setSaving(true);
    setError('');
    const body = {
      exception_type: type,
      tickets_number_change: needsTickets ? Number(tickets) : null,
      reason: reason.trim() || null,
    };
    try {
      if (editing) {
        await crmCampaignsAPI.updateTeslaException(initial.tournament_id, initial.user_id, body);
      } else {
        await crmCampaignsAPI.createTeslaException({
          ...body,
          user_id: Number(userId),
          tournament_id: Number(tournamentId),
        });
      }
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to save exception.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit exception' : 'Add exception'}</DialogTitle>
          <DialogDescription>
            A player can have one exception per tournament.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1.5 block">
              <FieldLabel>Tournament ID</FieldLabel>
              <Input inputMode="numeric" value={tournamentId} disabled={editing}
                onChange={(e) => setTournamentId(e.target.value)} />
            </label>
            <label className="space-y-1.5 block">
              <FieldLabel>User ID</FieldLabel>
              <Input inputMode="numeric" value={userId} disabled={editing}
                onChange={(e) => setUserId(e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <FieldLabel>Exception type</FieldLabel>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXCEPTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="space-y-1.5 block">
              <FieldLabel>Tickets</FieldLabel>
              <Input inputMode="numeric" value={needsTickets ? tickets : ''} disabled={!needsTickets}
                placeholder={needsTickets ? '' : 'n/a'} onChange={(e) => setTickets(e.target.value)} />
            </label>
          </div>
          {needsTickets && ticketsTooMany && (
            <p className="text-xs font-medium text-negative">Tickets can be at most {MAX_TICKETS.toLocaleString()}.</p>
          )}
          {needsConfirm && (
            <label className="space-y-1.5 block">
              <FieldLabel>Confirm tickets</FieldLabel>
              <Input inputMode="numeric" value={ticketsAgain} onChange={(e) => setTicketsAgain(e.target.value)} />
              <p className={`text-xs font-medium ${ticketsAgain.trim() && !confirmed ? 'text-negative' : 'text-ink-dim'}`}>
                {ticketsAgain.trim() && !confirmed
                  ? "The numbers don't match."
                  : `That's over ${CONFIRM_TICKETS.toLocaleString()} tickets; type the number again to confirm.`}
              </p>
            </label>
          )}
          <label className="space-y-1.5 block">
            <div className="flex items-center justify-between">
              <FieldLabel>Reason</FieldLabel>
              <span className={`text-[10px] font-medium ${reasonTooLong ? 'text-negative' : 'text-ink-mute'}`}>
                {reasonBytes}/{MAX_REASON_BYTES}
              </span>
            </div>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add exception'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ row, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setError(''); }, [row]);

  const confirm = async () => {
    setDeleting(true);
    try {
      await crmCampaignsAPI.deleteTeslaException(row.tournament_id, row.user_id);
      onDeleted();
    } catch (err) {
      setError(apiError(err, 'Failed to delete exception.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={Boolean(row)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete exception?</DialogTitle>
          <DialogDescription>
            {row && `User ${row.user_id} in tournament ${row.tournament_id}. This cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TeslaTicketsPanel = () => {
  // The ID inputs are typed into; debounce them so each keystroke is not a warehouse query.
  const [idInputs, setIdInputs] = useState({ tournament_id: '', user_id: '' });
  const [filters, setFilters] = useState({ tournament_id: '', user_id: '', exception_type: 'all' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ exceptions: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = { page, page_size: PAGE_SIZE };
    if (isPositiveInt(filters.tournament_id)) params.tournament_id = filters.tournament_id.trim();
    if (isPositiveInt(filters.user_id)) params.user_id = filters.user_id.trim();
    if (filters.exception_type !== 'all') params.exception_type = filters.exception_type;
    try {
      const res = await crmCampaignsAPI.listTeslaExceptions(params);
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Failed to load exceptions.'));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.tournament_id === idInputs.tournament_id && f.user_id === idInputs.user_id
        ? f : { ...f, ...idInputs }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [idInputs]);

  const setFilter = (key) => (value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const openAdd = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row) => { setEditing(row); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  // Deleting the last row of the last page leaves the page past the end; step back to the new
  // last page (which reloads it) rather than show an empty one.
  useEffect(() => {
    if (!loading && page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1.5 block">
          <FieldLabel>Tournament ID</FieldLabel>
          <Input className="h-8 w-40 text-xs" inputMode="numeric" value={idInputs.tournament_id}
            onChange={(e) => setIdInputs((f) => ({ ...f, tournament_id: e.target.value }))} />
        </label>
        <label className="space-y-1.5 block">
          <FieldLabel>User ID</FieldLabel>
          <Input className="h-8 w-40 text-xs" inputMode="numeric" value={idInputs.user_id}
            onChange={(e) => setIdInputs((f) => ({ ...f, user_id: e.target.value }))} />
        </label>
        <div className="space-y-1.5">
          <FieldLabel>Change type</FieldLabel>
          <Select value={filters.exception_type} onValueChange={setFilter('exception_type')}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {EXCEPTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button size="sm" onClick={openAdd}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add exception
        </Button>
      </div>

      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : data.exceptions.length === 0 ? (
            <p className="py-10 text-center text-sm font-medium text-ink-dim">No exceptions found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tournament ID</TableHead>
                  <TableHead>User ID</TableHead>
                  <TableHead>Change type</TableHead>
                  <TableHead className="text-right">Tickets</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.exceptions.map((row) => {
                  const info = typeInfo(row.exception_type);
                  return (
                    <TableRow key={`${row.tournament_id}-${row.user_id}`}>
                      <TableCell className="font-mono text-xs">{row.tournament_id}</TableCell>
                      <TableCell className="font-mono text-xs">{row.user_id}</TableCell>
                      <TableCell>
                        <Badge variant={info?.badge || 'secondary'}>{info?.label || row.exception_type}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-bold">{row.tickets_number_change ?? '—'}</TableCell>
                      <TableCell className="text-xs text-ink-dim max-w-xs">
                        <span className="line-clamp-2" title={row.reason || ''}>{row.reason || '—'}</span>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(row)} aria-label="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs font-medium text-ink-dim">
        <span>{data.total} exception{data.total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>

      <ExceptionFormDialog
        open={formOpen}
        initial={editing}
        onClose={closeForm}
        onSaved={() => { closeForm(); load(); }}
      />
      <DeleteDialog
        row={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => { setDeleting(null); load(); }}
      />
    </div>
  );
};

export default TeslaTicketsPanel;
